/**
 * 公用 HTTP 请求封装（基于 fetch）
 * 基础地址：VUE_APP_API_BASE_URL → 未配置时使用 src/config/api.js 中的默认值
 */

import { DEFAULT_API_BASE_URL } from '@/config/api'

const DEFAULT_TIMEOUT_MS = 30000

/** 去掉末尾斜杠，避免拼接时出现双斜杠 */
function normalizeBaseUrl(base) {
  const s = String(base ?? '').trim()
  if (!s) return ''
  return s.replace(/\/+$/, '')
}

/**
 * same-origin / 空字符串：开发态走 vue-cli 同源代理（见 vue.config.js）
 * 未配置：回落到 DEFAULT_API_BASE_URL
 */
function resolveApiBaseUrl() {
  // Vue CLI 会注入 .env 里写过的键；用 same-origin 明确表示相对路径
  if (!Object.prototype.hasOwnProperty.call(process.env, 'VUE_APP_API_BASE_URL')) {
    return normalizeBaseUrl(DEFAULT_API_BASE_URL)
  }
  const raw = String(process.env.VUE_APP_API_BASE_URL ?? '').trim()
  if (!raw || raw === 'same-origin' || raw === '/') return ''
  return normalizeBaseUrl(raw)
}

const API_BASE_URL = resolveApiBaseUrl()

/** 调试用：当前实际使用的 API 根地址 */
export function getApiBaseUrl() {
  return API_BASE_URL
}

import { getToken, clearAuth } from '@/utils/auth'

export function getAccessToken() {
  return getToken() || ''
}

/** @deprecated 请使用 auth.js 的 saveAuth / clearAuth */
export function setAccessToken(token) {
  if (!token) clearAuth()
}

let unauthorizedRedirecting = false

function isUnauthorizedPayload(status, body) {
  if (status === 401) return true
  if (!body || typeof body !== 'object') return false
  const code = body.code
  if (code === 401 || code === '401') return true
  const msg = String(body.message || body.msg || '')
  return /令牌无效|已过期|未登录|Unauthorized/i.test(msg)
}

/** 401 / 令牌失效：清 token 并跳转登录（并发请求只跳一次） */
function redirectToLogin() {
  if (unauthorizedRedirecting) return
  unauthorizedRedirecting = true
  clearAuth()
  import('@/router')
    .then(({ default: router }) => {
      if (router.currentRoute.value.name !== 'login') {
        router.replace({ name: 'login' })
      }
    })
    .finally(() => {
      window.setTimeout(() => {
        unauthorizedRedirecting = false
      }, 800)
    })
}

function buildUrl(path) {
  const p = path.startsWith('/') ? path : `/${path}`
  // 开发同源代理时 API_BASE_URL 为空，走当前站点 /api/...
  return `${API_BASE_URL}${p}`
}

function isNetworkFailure(err) {
  if (!err) return false
  if (err.name === 'TypeError') return true
  const msg = String(err.message || err || '')
  return /Failed to fetch|NetworkError|ERR_CONNECTION|ECONNRESET|ECONNREFUSED|load failed/i.test(
    msg,
  )
}

function networkFailureMessage(url, err) {
  const detail = String(err?.message || err || '').trim()
  const hostHint = API_BASE_URL
    ? `目标 ${API_BASE_URL}`
    : '目标为当前站点代理 /api（见 vue.config.js）'
  return `上传/请求网络中断（${hostHint}）。${detail ? detail + '。' : ''}请确认后端可达后重试`
}

/** 判断错误是否为「被主动取消」（区别于请求超时） */
export function isAbortError(err) {
  return err?.name === 'AbortError'
}

/** 无需携带 token 的接口（如登录、注册） */
const AUTH_SKIP_PATHS = ['/api/v1/auth/login']

function shouldAttachToken(path, skipToken) {
  if (skipToken) return false
  const p = path.startsWith('/') ? path : `/${path}`
  return !AUTH_SKIP_PATHS.some((authPath) => p === authPath || p.startsWith(`${authPath}/`))
}

function mergeHeaders(extra, { path, skipToken } = {}) {
  const headers = new Headers({
    Accept: 'application/json',
    ...extra,
  })
  const token = getAccessToken()
  if (token && shouldAttachToken(path, skipToken) && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  return headers
}

function parseResponseBody(response) {
  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    return response.json()
  }
  return response.text()
}

/** nginx 413 等非 JSON 错误转成可读文案 */
function httpErrorMessage(status, parsed) {
  if (parsed && typeof parsed === 'object' && (parsed.message || parsed.msg)) {
    return parsed.message || parsed.msg
  }
  const text = typeof parsed === 'string' ? parsed : ''
  if (status === 413 || /413|Request Entity Too Large/i.test(text)) {
    return '文件过大，前置 Nginx 拦截（413）。请把 lc.k2yun.com / lcadmin.k2yun.com 的 client_max_body_size 改为 520m 后 reload'
  }
  return `请求失败（${status}）`
}

/**
 * 从常见后端包装结构中提取业务数据
 * 支持：直接返回体 / { data } / { code, data, message }
 */
export function unwrapResponseData(body) {
  if (body == null || typeof body !== 'object') return body
  if ('code' in body || 'success' in body) {
    const code = body.code
    const ok =
      body.success === true ||
      code === 0 ||
      code === 200 ||
      code === '0' ||
      code === '200'
    if (!ok && code !== undefined && code !== null) {
      const msg = body.message || body.msg || '请求失败'
      if (isUnauthorizedPayload(null, body)) {
        redirectToLogin()
      }
      throw new Error(msg)
    }
    if ('data' in body && body.data != null) return body.data
    if ('result' in body && body.result != null) return body.result
  }
  if ('data' in body && body.data != null && Object.keys(body).length <= 3) {
    return body.data
  }
  if ('result' in body && body.result != null && Object.keys(body).length <= 3) {
    return body.result
  }
  return body
}

/**
 * @param {string} path 如 /api/v1/product-styles
 * @param {RequestInit & { params?: Record<string, string|number|boolean|undefined|null>, timeout?: number, skipToken?: boolean, signal?: AbortSignal }} options
 */
export async function request(path, options = {}) {
  const {
    params,
    timeout = DEFAULT_TIMEOUT_MS,
    headers: headerInit,
    body,
    skipToken,
    signal: externalSignal,
    ...rest
  } = options

  let url = buildUrl(path)
  if (params && Object.keys(params).length) {
    const search = new URLSearchParams()
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        search.append(key, String(val))
      }
    })
    const qs = search.toString()
    if (qs) url += (url.includes('?') ? '&' : '?') + qs
  }

  const headers = mergeHeaders(headerInit, { path, skipToken })
  let payload = body

  if (body != null && typeof body === 'object' && !(body instanceof FormData)) {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
    payload = JSON.stringify(body)
  }

  const controller = new AbortController()
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeout)

  // 外部可取消（翻页/切 tab 时中止上一批补拉请求），超时与外部取消互不干扰
  const onExternalAbort = () => controller.abort()
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort()
    else externalSignal.addEventListener('abort', onExternalAbort)
  }

  try {
    const response = await fetch(url, {
      ...rest,
      headers,
      body: payload,
      signal: controller.signal,
    })

    const parsed = await parseResponseBody(response)

    if (!response.ok) {
      const msg = httpErrorMessage(response.status, parsed)
      if (isUnauthorizedPayload(response.status, parsed)) {
        redirectToLogin()
      }
      throw new Error(msg || '未登录或登录已过期，请先登录')
    }

    return unwrapResponseData(parsed)
  } catch (err) {
    if (err.name === 'AbortError') {
      // 外部主动取消时抛出可识别的取消错误，避免被当成超时
      if (!timedOut && externalSignal?.aborted) {
        const abortErr = new Error('请求已取消')
        abortErr.name = 'AbortError'
        throw abortErr
      }
      throw new Error('请求超时，请稍后重试')
    }
    if (isNetworkFailure(err)) {
      throw new Error(networkFailureMessage(url, err))
    }
    throw err
  } finally {
    clearTimeout(timer)
    if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort)
  }
}

export function get(path, options) {
  return request(path, { ...options, method: 'GET' })
}

export function post(path, body, options) {
  return request(path, { ...options, method: 'POST', body })
}

/** multipart 上传（勿设置 Content-Type，由浏览器自动带 boundary） */
export function postForm(path, formData, options) {
  return request(path, { ...options, method: 'POST', body: formData })
}

/**
 * 大文件 multipart 上传（XHR，避免部分环境下 fetch 传大 body 中途 Failed to fetch）
 * @param {string} path
 * @param {FormData} formData
 * @param {{ timeout?: number, skipToken?: boolean, onProgress?: (percent: number) => void }} [options]
 */
export function postFormLarge(path, formData, options = {}) {
  const { timeout = DEFAULT_TIMEOUT_MS, skipToken, onProgress } = options
  const url = buildUrl(path)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url, true)
    xhr.timeout = timeout
    xhr.responseType = 'text'
    xhr.setRequestHeader('Accept', 'application/json')

    const token = getAccessToken()
    if (token && shouldAttachToken(path, skipToken)) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    }

    if (typeof onProgress === 'function' && xhr.upload) {
      xhr.upload.onprogress = (ev) => {
        if (!ev.lengthComputable) return
        const percent = Math.min(100, Math.round((ev.loaded / ev.total) * 100))
        onProgress(percent)
      }
    }

    xhr.onload = () => {
      const status = xhr.status
      let parsed = xhr.responseText
      try {
        parsed = parsed ? JSON.parse(parsed) : null
      } catch {
        /* 非 JSON 时保留原文 */
      }

      if (status >= 200 && status < 300) {
        try {
          resolve(unwrapResponseData(parsed))
        } catch (err) {
          reject(err)
        }
        return
      }

      const msg =
        (parsed && typeof parsed === 'object' && (parsed.message || parsed.msg)) ||
        `请求失败（${status}）`
      if (isUnauthorizedPayload(status, parsed)) {
        redirectToLogin()
      }
      reject(new Error(msg || '未登录或登录已过期，请先登录'))
    }

    xhr.onerror = () => {
      reject(new Error(networkFailureMessage(url, new TypeError('Failed to fetch'))))
    }

    xhr.ontimeout = () => {
      reject(new Error('请求超时，请稍后重试'))
    }

    xhr.onabort = () => {
      reject(new Error('上传已取消'))
    }

    xhr.send(formData)
  })
}

export function put(path, body, options) {
  return request(path, { ...options, method: 'PUT', body })
}

export function patch(path, body, options) {
  return request(path, { ...options, method: 'PATCH', body })
}

/** PATCH 无请求体（如标记已打印 /orders/{id}/printed） */
export function patchEmpty(path, options) {
  return request(path, { ...options, method: 'PATCH' })
}

export function del(path, options) {
  return request(path, { ...options, method: 'DELETE' })
}
